import { useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Grid, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  getAddressDispay,
  getDateDisplay,
  getSicDisplay,
  getValueDisplay,
} from "../../data/utilities";
import { AdvancedSearchFilters, getResource } from "../../data/request";
import { CompanyDetails } from "../../types/CompanyDetails";
import { File } from "../../types/File";
import { ItemCard } from "../Components/ItemCard";
import { ProPill } from "../Components/ProPill";
import { useAuth } from "../../auth/AuthContext";
import { BORDER_GREY, DELTA_ACCENT, DELTA_PRIMARY, STATUS_AMBER, STATUS_RED, TEXT_SECONDARY } from "../colors";

// Shown in place of real filing history rows for non-Pro/signed-out visitors -
// generic, plausible category labels (not this company's actual data) so the
// timeline's shape/purpose is still legible while genuinely gated content
// stays hidden, matching the "hint, don't hide" pattern used elsewhere.
const PLACEHOLDER_RECENT_CHANGES = [
  "Confirmation statement filed",
  "Accounts filed",
  "Officer appointed",
];

const PRO_FEATURE_TOOLTIP = "Advanced Search is a Pro or Enterprise feature - sign in for instant access";

// "No" is the routine, expected answer for these two - plain text like every
// other field. "Yes" is genuinely worth noticing, so it gets a chip instead
// of just blending in as the same plain text.
const yesNoDisplay = (value: boolean) =>
  value ? (
    <Chip label="Yes" size="small" style={{ backgroundColor: STATUS_AMBER, color: "white" }} />
  ) : (
    "No"
  );

// Overdue is a live, active compliance problem (unlike the historical
// liquidation/insolvency flags), so it earns red rather than amber - and it
// reads better as a pill next to the due date itself than as its own
// separate name/value row.
const dueDateDisplay = (date: string, overdue: boolean) => (
  <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
    {getDateDisplay(date)}
    {overdue && <Chip label="Overdue" size="small" style={{ backgroundColor: STATUS_RED, color: "white" }} />}
  </Box>
);

export const BasicInfo = (props: { companyDetails: CompanyDetails }) => {
  const { companyDetails } = props;
  const auth = useAuth();

  const [recentChanges, setRecentChanges] = useState<File[]>([]);
  const [recentChangesLoading, setRecentChangesLoading] = useState(true);

  // Recent Changes reuses the exact same filing-history resource the Filing
  // History tab fetches (see FilingHistoryTable.tsx's getResource(link) call)
  // - just the first page, trimmed down to the most recent handful of
  // entries for a compact summary instead of the full paginated table. Only
  // fetched for Pro users - non-Pro visitors see a static placeholder below,
  // so there's no point spending the network call on data they can't see.
  useEffect(() => {
    const link = companyDetails.links?.filing_history;
    if (!auth.isPro || !link) {
      setRecentChangesLoading(false);
      return;
    }
    setRecentChangesLoading(true);
    getResource(link).then((doc) => {
      const fetched: File[] = doc?.items ?? [];
      const sorted = [...fetched].sort((a, b) => ((a.date ?? "") > (b.date ?? "") ? -1 : 1));
      setRecentChanges(sorted.slice(0, 6));
      setRecentChangesLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isPro, companyDetails.links?.filing_history]);

  // Related Searches: a small set of deep links into Advanced Search, each
  // pre-filled with a filter derived straight from data already on this page
  // - only offered when the company actually has a usable value for it.
  const primaryLocation = companyDetails.registered_office_address?.locality;
  const primarySicCode = companyDetails.sic_codes?.[0];

  const relatedSearches: { label: string; filters: AdvancedSearchFilters }[] = [];
  if (primaryLocation) {
    relatedSearches.push({
      label: `Companies in ${primaryLocation}`,
      filters: { location: primaryLocation },
    });
  }
  if (primarySicCode) {
    relatedSearches.push({
      label: `Companies with SIC ${primarySicCode}`,
      filters: { sicCode: primarySicCode },
    });
  }

  const items = [];
  items.push({
    title: "Company Type",
    subtitle: getValueDisplay(companyDetails.type),
    icon: "type",
  });
  items.push({
    title: "Date of Creation",
    subtitle: getDateDisplay(companyDetails.date_of_creation),
    icon: "date",
  });
  items.push({
    title: "Jurisdiction",
    subtitle: getValueDisplay(companyDetails.jurisdiction),
    icon: "jurisdiction",
  });
  if (companyDetails.accounts?.last_accounts?.made_up_to) {
    items.push({
      title: "Last Accounts Made Up To",
      subtitle: getDateDisplay(companyDetails.accounts.last_accounts.made_up_to),
      icon: "date",
    });
  }
  if (companyDetails.confirmation_statement) {
    items.push({
      title: "Confirmation Statement Next Due",
      subtitle: dueDateDisplay(
        companyDetails.confirmation_statement.next_due,
        companyDetails.confirmation_statement.overdue ?? false
      ),
      icon: "date",
    });
  }
  if (companyDetails.accounts?.next_accounts?.due_on) {
    items.push({
      title: "Accounts Next Due",
      subtitle: dueDateDisplay(companyDetails.accounts.next_accounts.due_on, companyDetails.accounts?.overdue ?? false),
      icon: "date",
    });
  }
  items.push({
    // Companies House omits this field entirely for some companies (older
    // records, mostly) rather than sending an explicit false - since every
    // company where it IS present shows false unless genuinely liquidated,
    // "missing" reads as "No" here, not as truly unknown.
    title: "Has been liquidated:",
    subtitle: yesNoDisplay(companyDetails.has_been_liquidated ?? false),
    icon: "status",
  });
  items.push({
    title: "Has Insolvency History:",
    subtitle: yesNoDisplay(companyDetails.has_insolvency_history ?? false),
    icon: "status",
  });
  items.push({
    title: "Registered Office",
    subtitle: (
      <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {getAddressDispay(companyDetails.registered_office_address)}
        {companyDetails.registered_office_is_in_dispute && (
          <Chip label="In Dispute" size="small" style={{ backgroundColor: STATUS_RED, color: "white" }} />
        )}
      </Box>
    ),
    icon: "address",
  });

  return (
    <Box>
      <Box sx={styles.containerStyle}>
        {items.map((item) => (
          <ItemCard item={item} key={item.title} />
        ))}
      </Box>

      {companyDetails.sic_codes && companyDetails.sic_codes.length > 0 && (
        <Box style={{ padding: 10, marginTop: 10 }}>
          <Typography
            variant="subtitle2"
            style={{ color: TEXT_SECONDARY, marginBottom: 8 }}
          >
            Nature of Business (SIC)
          </Typography>
          <Grid container spacing={1}>
            {companyDetails.sic_codes.map((code) => (
              <Grid item key={code}>
                <Chip
                  label={getSicDisplay(code)}
                  variant="outlined"
                  clickable
                  component={RouterLink}
                  to="/search/-"
                  state={{ filters: { sicCode: code } }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {companyDetails.previous_company_names &&
        companyDetails.previous_company_names.length > 0 && (
          <Box style={{ padding: 10, marginTop: 10 }}>
            <Typography
              variant="subtitle2"
              style={{ color: TEXT_SECONDARY, marginBottom: 16 }}
            >
              Company Name History
            </Typography>
            <Box style={{ position: "relative", paddingLeft: 28 }}>
              <Box
                style={{
                  position: "absolute",
                  left: 5,
                  top: 6,
                  bottom: 6,
                  width: 2,
                  backgroundColor: BORDER_GREY,
                }}
              />
              <Box style={{ position: "relative", marginBottom: 20 }}>
                <Box
                  style={{
                    position: "absolute",
                    left: -28,
                    top: 3,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: DELTA_ACCENT,
                    border: `2px solid ${DELTA_ACCENT}`,
                  }}
                />
                <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography variant="body1" style={{ fontWeight: 700, color: DELTA_PRIMARY }}>
                    {companyDetails.company_name}
                  </Typography>
                  <Chip label="Current" size="small" style={{ backgroundColor: DELTA_ACCENT, color: "white" }} />
                </Box>
                <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                  {getDateDisplay(
                    companyDetails.previous_company_names[0].ceased_on
                  )}{" "}
                  – Present
                </Typography>
              </Box>
              {companyDetails.previous_company_names.map((previous, index, all) => (
                <Box
                  key={previous.name + previous.ceased_on}
                  style={{ position: "relative", marginBottom: index === all.length - 1 ? 0 : 20 }}
                >
                  <Box
                    style={{
                      position: "absolute",
                      left: -28,
                      top: 3,
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      border: `2px solid ${DELTA_ACCENT}`,
                    }}
                  />
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    {previous.name}
                  </Typography>
                  <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                    {getDateDisplay(previous.effective_from)} – {getDateDisplay(previous.ceased_on)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

      {relatedSearches.length > 0 && (
        <Box style={{ padding: 10, marginTop: 10 }}>
          <Box style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Typography variant="subtitle2" style={{ color: TEXT_SECONDARY }}>
              Related Searches
            </Typography>
            {!auth.isPro && <ProPill />}
          </Box>
          <Grid container spacing={1}>
            {relatedSearches.map((search) =>
              auth.isPro ? (
                <Grid item key={search.label}>
                  <Chip
                    label={search.label}
                    variant="outlined"
                    clickable
                    component={RouterLink}
                    to="/search/-"
                    state={{ filters: search.filters }}
                  />
                </Grid>
              ) : (
                <Grid item key={search.label}>
                  <Tooltip title={PRO_FEATURE_TOOLTIP}>
                    <span>
                      <Chip
                        label={search.label}
                        variant="outlined"
                        disabled
                        component={RouterLink}
                        to="/search/-"
                        state={{ filters: search.filters }}
                      />
                    </span>
                  </Tooltip>
                </Grid>
              )
            )}
          </Grid>
        </Box>
      )}

      <Box style={{ padding: 10, marginTop: 10 }}>
        <Box style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Typography variant="subtitle2" style={{ color: TEXT_SECONDARY }}>
            Recent Changes
          </Typography>
          {!auth.isPro && <ProPill />}
        </Box>

        {!auth.isPro ? (
          <Tooltip title="Recent Changes is a Pro or Enterprise feature - sign in for instant access">
            <Box style={{ position: "relative", paddingLeft: 28, opacity: 0.5, cursor: "not-allowed" }}>
              <Box
                style={{
                  position: "absolute",
                  left: 5,
                  top: 6,
                  bottom: 6,
                  width: 2,
                  backgroundColor: BORDER_GREY,
                }}
              />
              {PLACEHOLDER_RECENT_CHANGES.map((label, index, all) => (
                <Box key={label} style={{ position: "relative", marginBottom: index === all.length - 1 ? 0 : 20 }}>
                  <Box
                    style={{
                      position: "absolute",
                      left: -28,
                      top: 3,
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      border: `2px solid ${DELTA_ACCENT}`,
                    }}
                  />
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                    ••••••••
                  </Typography>
                </Box>
              ))}
            </Box>
          </Tooltip>
        ) : recentChangesLoading ? (
          <Box style={{ display: "flex", justifyContent: "center", padding: 20 }}>
            <CircularProgress color="primary" size={24} />
          </Box>
        ) : recentChanges.length === 0 ? (
          <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
            No recent filing history available.
          </Typography>
        ) : (
          <Box style={{ position: "relative", paddingLeft: 28 }}>
            <Box
              style={{
                position: "absolute",
                left: 5,
                top: 6,
                bottom: 6,
                width: 2,
                backgroundColor: BORDER_GREY,
              }}
            />
            {recentChanges.map((item, index, all) => (
              <Box
                key={item.transaction_id}
                style={{ position: "relative", marginBottom: index === all.length - 1 ? 0 : 20 }}
              >
                <Box
                  style={{
                    position: "absolute",
                    left: -28,
                    top: 3,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: index === 0 ? DELTA_ACCENT : "white",
                    border: `2px solid ${DELTA_ACCENT}`,
                  }}
                />
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {getValueDisplay(item.description)}
                </Typography>
                <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                  {getDateDisplay(item.date)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const styles = {
  // Grid, not flex-wrap: a fixed set of column tracks means one card with a
  // long title (e.g. "Confirmation Statement Next Due") just wraps onto a
  // second line within its own cell, instead of growing wider than the rest
  // and throwing off row alignment for every card after it.
  containerStyle: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "20px", // matches the 10px margin each card previously had on every side (10+10 between neighbors)
    padding: "10px",
  },
};
